import type { MetricKey } from '../analytics.js';
import type { AgentConnectState, AgentConnectTarget } from '../agent-connect.js';
import type { MetricsCardExtras } from './analysis.js';
import type { CreativeCardExtras } from './creative.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ÇEKİRDEK KARTLAR — Faz 8'den önce var olan altı tür (30 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `agent.ts`ten taşındı; birliğin tamamı ve "yeni tür nasıl eklenir" tarifi
 * `./index.ts` başlığında. Bu dosya altyapı ajanının — alan ajanları DOKUNMAZ.
 *
 * İki tür alan ajanına AİT ama tipi burada: `metrics` (analiz) ve `creative`
 * (kreatif). Onları geliştirmek için bu dosya DEĞİŞMEZ; alan dosyasındaki
 * `MetricsCardExtras` / `CreativeCardExtras` genişletilir (kesişim — alanlar
 * İSTEĞE BAĞLI olmalı ki eski satırlardaki kartlar geçerli kalsın). Görünümleri
 * de alan dosyasında: web `tool-renderers/analysis.tsx` · `creative.tsx`.
 *
 * 🔴 KİŞİSEL VERİ YOK. Kart sohbet satırına (`toolCalls[].ui`) KALICI yazılıyor
 * ve sohbet geçmişiyle yıllarca duruyor. Lead/yorum yazarı/ekip e-postası/
 * sipariş müşterisi gibi veri kartta yalnız KİMLİK ve SAYI olarak durur;
 * görüntü verisi çizim anında canlı uçtan gelir (`GET /agent/cards/:id/data`).
 * Sunucu her kartı kaydetmeden önce kişisel veri kemerinden geçiriyor
 * (`apps/api/src/agent/cards/finalize.ts`) — takılan kart hiç çizilmez.
 */

export type CoreAgentCard =
  /**
   * Şıklı soru.
   *
   * `text` BURADA da duruyor ama arayüz onu BASMIYOR: soruyu model zaten
   * cümlesinin içinde yazıyor ve iki kez basmak 22 Ağu 2026'da bir kez
   * düzeltilmiş bir hataydı. Alan yine de taşınıyor — cevaplanmış kartın
   * neyin cevabı olduğu geçmişte okunabilmeli.
   */
  | { kind: 'ask'; text: string; options: string[]; answered: string | null }
  /**
   * ÜRÜN SEÇİCİ — `list_products` sonucunun dokunulabilir hâli.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden ayrı bir "seç" aracı YOK
   * ═══════════════════════════════════════════════════════════════════════
   * Ürün listesinin doğru ekran biçimi zaten bir ızgaradır — modelin onu
   * cümle içinde saymasının hiçbir üstünlüğü yok. Kart listenin KENDİSİ;
   * dokunmak da o listeye verilen cevap. Ayrı bir `pick_product` aracı,
   * modele "şimdi seçtireyim mi" diye ikinci bir karar verdirirdi ve o
   * kararı yanlış verdiği her turda kullanıcı düz metin okurdu.
   *
   * 🔴 `imageUrl` KARTA YAZILABİLİR: mağaza CDN'inin açık adresi, imzalı
   * değil. Kreatif önizlemeleri öyle DEĞİL — gerekçe `creative` dalında.
   */
  | {
      kind: 'product';
      products: {
        productId: string;
        title: string;
        priceRange: string | null;
        imageUrl: string | null;
        platform: string;
        /// Reklamı engelleyen durum: görselsiz ya da adressiz ürün seçilemez.
        blocker: 'noImage' | 'noUrl' | null;
      }[];
      /// Kaç satır gizlendi — kullanıcı listenin tamamı sanmasın.
      total: number;
      picked: { productId: string; title: string; imageUrl: string | null } | null;
    }
  /**
   * KREATİF SEÇİCİ — ÇOKLU seçim.
   *
   * Her kreatif BİR REKLAM olacağı için seçim tekil değil: `create_campaign`
   * `creativeIds` istiyor ve kullanıcı "şu üçüyle açalım" diyebilmeli.
   *
   * 🔴 ÖNİZLEME ADRESİ TAŞINMIYOR. `CreativeRow.thumbUrl` kısa ömürlü bir
   * imzalı adres; kalıcı bir karta yazılsaydı iki saat sonra sessizce kırık
   * bir görsel olurdu. Kart kimlik taşıyor, arayüz adresleri çizim anında
   * `GET /creatives?ids=` ile tazeliyor.
   */
  | ({
      kind: 'creative';
      creatives: {
        creativeId: string;
        type: string;
        name: string | null;
        width: number | null;
        height: number | null;
      }[];
      picked: string[];
    } & CreativeCardExtras)
  /**
   * KPI BANDI — `get_metrics` sonucunun ekrandaki hâli (19 Eyl 2026).
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden model tablo YAZMIYOR
   * ═══════════════════════════════════════════════════════════════════════
   * Kullanıcı "tablolarda, metinlerde hatalar var" diye bildirdi. Model bir
   * araç sonucunu markdown tablosuna çevirirken üç şeyi kaybediyordu: sayının
   * biçimi (1.240,50 mi 1240.5 mi), değişimin YÖNÜNÜN iyi mi kötü mü olduğu
   * (CPA'da düşüş iyi, ROAS'ta kötü) ve sütun hizası. Sunucu bu üçünü de
   * biliyor (`METRIC_DEFINITIONS.betterWhen`), model bilmiyor.
   *
   * Artık sayılar KARTTA, doğrulanmış kaynaktan; modelin cümlesi yorumu
   * taşıyor. İki yüzey aynı sayıyı iki kez basmasın diye sistem prompt'u
   * modele "kart zaten gösteriyor, tabloyu tekrar yazma" diyor.
   *
   * Sıra SUNUCUNUN: hangi metriklerin önce geleceği ürün kararı ve her
   * istemcide yeniden verilmemeli.
   */
  | ({
      kind: 'metrics';
      range: { from: string; to: string };
      compareRange: { from: string; to: string } | null;
      /// `null` + `mixedCurrency` → para bağlı metrikler "—" basılır.
      currency: string | null;
      mixedCurrency: boolean;
      items: {
        key: MetricKey;
        value: number | null;
        previous: number | null;
        deltaPct: number | null;
      }[];
      /// `DataState` — `empty` iken kart sayı değil durum cümlesi basar.
      state: string;
    } & MetricsCardExtras)
  /**
   * TABLO — kampanya listesi, sıralama, sipariş, anahtar kelime.
   *
   * Tek bir genel yüzey: sütun başına BİÇİM taşınıyor, ekran biçimi uyguluyor.
   * Her okuma aracı için ayrı bir kart bileşeni yazmak, dört tablo ve dört
   * ayrı hizalama kararı demekti (`ui-parcasi-bir-kez-yazilir`).
   *
   * 🔴 Satır sayısı KIRPILMIŞ gelebilir; `total` tamamını söylüyor ve kart
   * "N satır daha" yazıyor — kullanıcı listeyi eksiksiz sanmasın.
   */
  | {
      kind: 'table';
      /// Başlık ve bağlantı bundan: `dashboard.agentChat.table.<id>`.
      id: AgentTableId;
      range: { from: string; to: string } | null;
      currency: string | null;
      columns: { key: string; format: AgentTableFormat }[];
      rows: {
        id: string;
        cells: Record<string, string | number | null>;
        /// Sıralamada en iyi/en kötü grubu; diğer tablolarda `null`.
        group: 'best' | 'worst' | null;
      }[];
      total: number;
      /// Sıralama tablosunda neye göre sıralandığı — başlık ve vurgu bundan.
      metric: MetricKey | null;
    }
  /**
   * BAĞLANTI KARTI — `connect_platform` (30 Eyl 2026, `agent-connect.ts`).
   *
   * Platform logosu, kısa açıklama, güncel durum ve büyük "Bağlan" /
   * "Yeniden bağlan" / "Hesabı değiştir" düğmesi. Düğme bağlantılar sayfasının
   * uçlarını çağırıyor; OAuth dönüşü sohbetin kendi adresine geliyor ve kart
   * sonucu gösteriyor (reklamda hesap seçimi kartın İÇİNDE).
   *
   * 🔴 SIR TAŞIMAZ: yalnız platform + durum. Mağaza anahtarları kartın
   * formundan doğrudan API'ye gidiyor.
   *
   * Durum aracın çağrıldığı anın görüntüsü; dönüşte sunucu durumu yeniden
   * okuyup karta yazıyor (`completedAt`) — yenilemede kart "bağlandı" diyor.
   */
  | {
      kind: 'connect';
      target: AgentConnectTarget;
      /// `AdPlatform` ya da `StorePlatform` değeri.
      platform: string;
      state: AgentConnectState;
      /// Bağlı hesabın / mağazanın adı (platformdan gelen metin).
      account: string | null;
      /// Reklam bağlantısının kimliği — yeniden bağlama ve hesap seçimi bununla.
      connectionId: string | null;
      /// Yer demo hesapla dolu.
      demo: boolean;
      /// Mağazanın bağlanma kipi (`StorePlatformCard.authMode`); reklamda `null`.
      authMode: 'oauth' | 'apiKey' | null;
      /// Platformun resmî reklam yöneticisi adresi (reklamda) — "hesabın yok mu?".
      signupUrl: string | null;
      /// Sunucu dönüşte durumu yeniden okuyup yazdıysa o an; ilk kartta `null`.
      completedAt: string | null;
    }
  /**
   * KAYITLAR — özel kartı OLMAYAN her okuma sonucunun genel görünümü (Faz 8
   * dalga 2, sohbet çekirdeği).
   *
   * Araç izleri sohbetten kalktıktan sonra (30 Eyl 2026) kartsız bir okuma
   * sonucu ekranda HİÇ görünmüyordu: model "şu kuralları buldum" diyor,
   * kullanıcı neyin bulunduğunu göremiyordu. Bu kart sonucun kendisinden
   * kolon çıkarıyor (`apps/api/src/agent/cards/records.ts`) — katalog okuma
   * işlemi, ekran aracı ya da çekirdek okuma aracı. Özel kartı olan araç
   * (`toolCardKinds(ad)` dolu) bu karta HİÇ düşmüyor; tür bir araç adına
   * bağlı değil, o yüzden `CORE_TOOL_CARDS`ta satırı yok.
   *
   * 🔴 KİŞİSEL VERİ YOK: kaynak önce kalıcı kayıttaki kemerden geçiyor
   * (`redactToolResult` / ekran aracının kendi gizlemesi), gizlenen alan
   * kolon olarak ÇİZİLMİYOR ve sayısı `hiddenFields`te duruyor; sonra kart
   * `finalizeCard`tan geçiyor. Hücreler DÜZ METİN — platform/müşteri yazısı
   * markdown/HTML olarak çizilmez.
   *
   * 🔴 KATALOG İŞLEMİNDE PARA BİÇİMİ YOK: ham platform sonuçlarında tutar
   * birimi platforma göre değişiyor (Meta bütçesi kuruş, Google `*_micros`);
   * kart sayıyı olduğu gibi basıyor — yanlış birimle biçimlenmiş bir tutar,
   * ham sayıdan kötü. Mentorivo'nun KENDİ servisleri (ekran/çekirdek araç)
   * tutarı ana birimde ve yanında para birimiyle döndürüyor: orada `money`
   * (1 Eki 2026).
   */
  | {
      kind: 'records';
      /**
       * Sonucun kaynağı — başlık bundan. `op`: katalog işlemi (`name` işlem
       * kimliği, `title` iki dilli adı); `tool`: ekran/çekirdek aracı (`name`
       * araç adı — ekran `agentChat.tool.<name>` ile çevirir).
       */
      source: {
        type: 'op' | 'tool';
        name: string;
        title: { tr: string; en: string } | null;
        /// `AdPlatform` değeri — katalog işleminde; araçta `null`.
        platform: string | null;
      };
      /// `table` — kayıt listesi (satır = kayıt); `record` — tek nesne (satır = alan).
      layout: 'table' | 'record';
      /**
       * Kolon anahtarı sonuçtaki alan YOLU (`budget.amount`); ekran okunur
       * etikete çevirir (`cards.records.field.*`, yoksa okunur kelimeler).
       * 1 Eki 2026 biçimleri: `datetime` (saatli damga), `percent` (0–100
       * ölçekli yüzde: `sharePct`), `ratio` (0–1 oran → %: `impressionShare`),
       * `money` (yalnız Mentorivo servisi + bilinen para birimi — `currency`).
       */
      columns: {
        key: string;
        format: 'text' | 'number' | 'id' | 'date' | 'datetime' | 'boolean' | 'status' | 'percent' | 'ratio' | 'money';
        /// `money` kolonunun para birimi (ISO 4217); diğerlerinde yok.
        currency?: string | null;
      }[];
      rows: { id: string; cells: Record<string, string | number | boolean | null> }[];
      /// Kaynaktaki kayıt sayısı — kart ilk N'ini taşır (`rows.length`); fazlası "panelde".
      total: number;
      /// Kişisel veri olduğu için ÇİZİLMEYEN alan sayısı (ekran "N alan gizlendi" der).
      hiddenFields: number;
    };

export const AGENT_TABLE_IDS = ['campaigns', 'ranking', 'orders', 'keywords'] as const;
export type AgentTableId = (typeof AGENT_TABLE_IDS)[number];

/**
 * Hücre biçimi.
 *
 * `entity` bir satırın KİMLİĞİ: ad + platform işareti + durum. Hücre değeri
 * adı taşıyor; platform ve durum aynı satırın `platform` / `status`
 * hücrelerinden okunuyor ve ayrı sütun olarak ÇİZİLMİYOR.
 *
 * `money` satırın kendi para birimini (`currency` hücresi) yoksa kartınkini
 * kullanıyor. Karışık para biriminde toplam satırı yok zaten.
 */
export type AgentTableFormat =
  | 'entity'
  /// Sütun anahtarı bir `MetricKey`: birim ve basamak `METRIC_DEFINITIONS`ten.
  | 'metric'
  | 'text'
  | 'money'
  | 'number'
  | 'percent'
  | 'ratio'
  | 'date'
  | 'status';

/** Çekirdek türler — `AGENT_CARD_KINDS` bunlardan ve alan listelerinden kurulur. */
export const CORE_CARD_KINDS = ['ask', 'product', 'creative', 'metrics', 'table', 'connect', 'records'] as const;

/**
 * Araç adı → çizebileceği kart türleri (çekirdek araçlar + bağlantı kartı).
 * Web `lib/agent-tool-views.ts` alan listeleriyle birleştiriyor.
 */
export const CORE_TOOL_CARDS: Readonly<Record<string, readonly CoreAgentCard['kind'][]>> = {
  ask_user: ['ask'],
  list_products: ['product'],
  list_creatives: ['creative'],
  get_metrics: ['metrics'],
  get_campaigns: ['table'],
  analyze_campaigns: ['table'],
  list_orders: ['table'],
  list_keywords: ['table'],
  connect_platform: ['connect'],
};
